import { isGradient } from "@components/color-picker/lib/detect";
import { parseGradient } from "@components/color-picker/lib/parse";
import { serializeGradient } from "@components/color-picker/lib/serialize";

/**
 * Round-trip a gradient CSS string through the strict parser and re-serialize
 * it. Returns `null` for non-gradient input or any string that doesn't exactly
 * match the parser grammar.
 *
 * Use this before interpolating gradient strings into styled-components
 * templates so a stored value cannot break out of the CSS property.
 */
export const safeGradient = (value: string | undefined): string | null => {
	if (!value || !isGradient(value)) return null;
	const parsed = parseGradient(value);
	return parsed ? serializeGradient(parsed) : null;
};

/**
 * Normalize a border width to a CSS length. Numbers (and numeric strings)
 * are treated as pixels; anything else — notably a `var(--…)` reference —
 * is passed through untouched so callers can defer the width to a CSS
 * custom property.
 */
const toBorderLength = (width: number | string): string => {
	if (typeof width === "number") return `${width}px`;
	const trimmed = width.trim();
	return /^-?\d*\.?\d+$/.test(trimmed) ? `${trimmed}px` : trimmed;
};

/**
 * Returns a CSS string for border that handles both solid colors and gradients.
 * For gradients, uses a ::before pseudo-element with mask-composite so that
 * gradient borders respect border-radius (border-image cannot do rounded corners).
 *
 * Gradient values are round-tripped through the strict parser; malformed
 * gradient strings fall back to a transparent border.
 */
export const gradientBorderCss = (
	color: string | undefined,
	rawWidth: number | string,
): string => {
	if (!color) return "";
	const width = toBorderLength(rawWidth);
	if (isGradient(color)) {
		const safe = safeGradient(color);
		if (!safe) return `border: ${width} solid transparent;`;
		return `
      border: ${width} solid transparent;
      position: relative;
      &::before {
        content: '';
        position: absolute;
        inset: calc(-1 * ${width});
        border-radius: inherit;
        border: ${width} solid transparent;
        background: ${safe} border-box;
        -webkit-mask: linear-gradient(#000 0 0) padding-box, linear-gradient(#000 0 0);
        -webkit-mask-composite: xor;
        mask: linear-gradient(#000 0 0) padding-box, linear-gradient(#000 0 0);
        mask-composite: exclude;
        pointer-events: none;
      }
    `;
	}
	return `border: ${width} solid ${color};`;
};
